import { ActionCreator, createSlice } from '@reduxjs/toolkit';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';

import { getUserSelf, updateUserSelf } from '@/api/users';
import { auth } from '@/fire';
import { User } from '@/models/user';
import LocalStorageService from '@/services/LocalStorageService';

type State = {
  currentUser: User;
};

const trimUser = (user: User) => ({
  id: user.id,
  name: user.name,
  email: user.email,
  role: user.role,
  workspaceId: user.workspaceId,
  workspaceName: user.workspaceName,
  workspaceStorageTerms: user.workspaceStorageTerms
});

const initialState: State = {
  currentUser: JSON.parse(LocalStorageService.getItem('user'))
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, { payload }) => {
      state.currentUser = payload;
    }
  }
});

export const { setUser } = userSlice.actions;
export const currentUser = (state: { user: State }) => state.user.currentUser;

export const changeWorkspace =
  (workspaceId: number): ActionCreator<any> =>
  async () => {
    await updateUserSelf({ workspaceId });

    const user = JSON.parse(LocalStorageService.getItem('user') || '');
    const updatedUser = { ...user, workspaceId };
    LocalStorageService.setItem('user', JSON.stringify(updatedUser));
    window.location.href = '/';
  };

export const getUser = (): ActionCreator<any> => async (dispatch) => {
  const { data } = await getUserSelf();
  if (!data) throw new Error('User not found');

  const user = trimUser(data);
  LocalStorageService.setItem('user', JSON.stringify(user));
  dispatch(setUser(user));
  return data;
};

export const login =
  ({
    email,
    password
  }: {
    email: string;
    password: string;
  }): ActionCreator<any> =>
  async (dispatch) => {
    const fireBaseUserCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const { user: firebaseAuthUser } = fireBaseUserCredential;

    if (!firebaseAuthUser) {
      throw new Error('User authentication failed');
    }

    const data = await dispatch(getUser());
    return data;
  };

export const logout = (): ActionCreator<any> => async () => {
  // eslint-disable-next-line no-console
  signOut(auth).catch((error) => console.error('Error signing out:', error));
};

export default userSlice.reducer;
